import { useState } from "react";
import { Pencil, Save, X, Plus, Trash2, Loader2, Lightbulb, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { StepNav } from "./Stepper";
import KeywordsAdded from "./KeywordsAdded";
import ToolsMatch from "./ToolsMatch";
import { hasTool, cvText } from "../../lib/cvMatch";
import AskForChange from "./AskForChange";
import CvPaper from "./CvPaper";

function CvEditor({ app, onUpdate }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const cv = app.tailored_cv || {};
  const setExp = (ei, fn) => setDraft((d) => {
    const exp = [...d.experience];
    exp[ei] = { ...exp[ei], bullets: fn([...(exp[ei].bullets || [])]) };
    return { ...d, experience: exp };
  });
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/applications/${app.id}/cv`, { tailored_cv: draft });
      onUpdate({ tailored_cv: draft });
      setDraft(null);
      toast.success("Saved. Your downloads will use these edits.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your edits. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  if (!draft) {
    return (
      <div className="gi-card p-5 sm:p-6" data-testid="cv-edit-card">
        <h3 className="font-display text-lg font-bold text-ink">Make it sound like you</h3>
        <p className="text-[15px] text-body mt-1">Read the summary and bullet points. Change any wording you wouldn't say yourself.</p>
        <button data-testid="edit-cv-button" onClick={() => setDraft(JSON.parse(JSON.stringify(cv)))} className="mt-4 gi-btn gi-btn-secondary"><Pencil className="w-4 h-4" /> Edit my CV text</button>
      </div>
    );
  }
  return (
    <div className="gi-card p-5 sm:p-6 space-y-5" data-testid="cv-editor">
      <div>
        <label htmlFor="edit-summary" className="gi-label">Professional summary</label>
        <textarea id="edit-summary" data-testid="edit-summary-input" rows={4} value={draft.summary || ""} onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))} className="gi-input resize-y" />
      </div>
      {(draft.experience || []).map((e, ei) => (
        <div key={ei} className="rounded-xl border border-line bg-canvas p-4">
          <div className="text-[15px] font-semibold text-ink mb-3">{e.role} · {e.company}</div>
          <div className="space-y-2">
            {(e.bullets || []).map((b, bi) => (
              <div key={bi} className="flex gap-2 items-start">
                <label htmlFor={`b-${ei}-${bi}`} className="sr-only">Bullet {bi + 1}</label>
                <textarea id={`b-${ei}-${bi}`} data-testid={`edit-bullet-${ei}-${bi}`} rows={2} value={b} onChange={(ev) => setExp(ei, (bs) => { bs[bi] = ev.target.value; return bs; })} className="gi-input flex-1 resize-y" />
                <button data-testid={`remove-bullet-${ei}-${bi}`} aria-label="Remove bullet" onClick={() => setExp(ei, (bs) => bs.filter((_, j) => j !== bi))} className="mt-1 w-10 h-10 rounded-lg flex items-center justify-center text-faint hover:text-danger hover:bg-danger-soft"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid={`add-bullet-${ei}`} onClick={() => setExp(ei, (bs) => [...bs, ""])} className="gi-btn gi-btn-tertiary gi-btn-sm"><Plus className="w-4 h-4" /> Add a bullet</button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button data-testid="save-cv-edits-button" onClick={save} disabled={saving} className="gi-btn gi-btn-primary">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes</button>
        <button data-testid="cancel-cv-edits-button" onClick={() => setDraft(null)} disabled={saving} className="gi-btn gi-btn-secondary"><X className="w-4 h-4" /> Cancel</button>
      </div>
    </div>
  );
}

function Coaching({ app }) {
  const [tips, setTips] = useState(app.gap_coaching || null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${app.id}/gap-coaching`);
      setTips(data.coaching || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load tips just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gi-card border-l-[3px] border-l-kesh p-5 sm:p-6" data-testid="gap-coaching-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Lightbulb className="w-5 h-5 text-kesh" /> Tips for the skills you don't have yet</h3>
          <p className="text-[15px] text-body mt-1">Small, realistic ways to close each gap, like a short course or project.</p>
        </div>
        {!tips && <button data-testid="gap-coaching-button" onClick={load} disabled={busy} className="gi-btn gi-btn-kesh gi-btn-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Get tips</button>}
      </div>
      {tips?.length > 0 && (
        <ul className="mt-4 space-y-2.5" data-testid="gap-coaching-list">
          {tips.map((t, i) => <li key={i} className="rounded-xl bg-canvas border border-line p-3.5"><div className="text-sm font-semibold text-ink">{t.gap}</div><p className="text-[15px] text-body mt-0.5">{t.suggestion}</p></li>)}
        </ul>
      )}
      {tips && !tips.length && <p className="mt-3 text-[15px] text-body">No gaps to work on. Nice!</p>}
    </div>
  );
}

export default function StepImprove({ app, onUpdate, onBack, onNext }) {
  const changes = (app.recommendations || []).filter((r) => !/^gap:/i.test(r));
  const hasGaps = (app.missing_keywords || []).length > 0;
  const toolsOut = (app.job_tools || []).some((t) => !hasTool(cvText(app.tailored_cv), t));
  return (
    <div className="af-fade-up space-y-5" data-testid="studio-step-improve">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Improve your CV</h1>
        <p className="text-body mt-2 max-w-2xl" data-testid="improve-intro">{toolsOut
          ? "Some tools from the job ad aren't in your CV any more. Ask for a change below to add them back, or for any other change."
          : "Every keyword from the job ad is already in your CV. Ask for any other change and we'll rewrite it straight away."}</p>
      </div>
      <ToolsMatch app={app} testid="improve-tools-match" />
      <KeywordsAdded app={app} testid="improve-keywords-added" />
      <AskForChange app={app} onUpdate={onUpdate} />
      {changes.length > 0 && (
        <details className="gi-card p-5 sm:p-6 group" data-testid="changes-made">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
            <span className="font-display text-lg font-bold text-ink">What we changed ({changes.length})</span>
            <span className="text-sm text-brand group-open:hidden">Show</span><span className="text-sm text-brand hidden group-open:inline">Hide</span>
          </summary>
          <ul className="mt-4 space-y-2">
            {changes.map((r, i) => <li key={i} className="flex gap-2.5 text-[15px] text-body"><Check className="w-4 h-4 text-success mt-1 shrink-0" />{r}</li>)}
          </ul>
        </details>
      )}
      <CvEditor app={app} onUpdate={onUpdate} />
      <details className="gi-card p-5 sm:p-6 group" data-testid="improve-preview" open>
        <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
          <span className="font-display text-lg font-bold text-ink">Your CV right now</span>
          <span className="text-sm text-brand group-open:hidden">Show</span><span className="text-sm text-brand hidden group-open:inline">Hide</span>
        </summary>
        <div className="mt-4"><CvPaper cv={app.tailored_cv || {}} /></div>
      </details>
      {hasGaps && <Coaching app={app} />}
      <StepNav onBack={onBack} onNext={onNext} nextLabel="Next: preview & download" />
    </div>
  );
}
